/**
 * Cutfruit Design System — reusable custom select (combobox/listbox).
 * Drives snippets/ds-select.liquid. Delegated + idempotent so multiple
 * instances on a page (and re-renders via shopify:section:load) all work.
 *
 * Fires on the `[data-ds-select]` root:
 *   ds-select:change   { detail: { id, value } }  — user picked an option
 * Listens on the `[data-ds-select]` root:
 *   ds-select:set      { detail: { value } } — external state sync; updates
 *                        the trigger without firing :change.
 *   ds-select:options  { detail: { items: [{ value, label, meta, selected }] } }
 *                        — replaces the option list (e.g. when a consumer
 *                        derives options from another component's DOM).
 */
(function () {
  function getEls(root) {
    return {
      trigger: root.querySelector('[data-ds-select-trigger]'),
      panel: root.querySelector('[data-ds-select-panel]'),
      label: root.querySelector('[data-ds-select-label]'),
      meta: root.querySelector('[data-ds-select-meta]'),
      options: Array.from(root.querySelectorAll('[data-ds-select-option]')),
    };
  }

  function isOpen(root) {
    return root.classList.contains('ds-select--open');
  }

  function setActiveDescendant(root, option) {
    const { trigger } = getEls(root);
    if (option) {
      trigger.setAttribute('aria-activedescendant', option.id);
    } else {
      trigger.removeAttribute('aria-activedescendant');
    }
  }

  function openPanel(root, { focusOption } = {}) {
    const { trigger, panel, options } = getEls(root);
    root.classList.add('ds-select--open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;

    const active =
      options.find((o) => o.getAttribute('aria-selected') === 'true') ||
      options[0];
    if (active) {
      options.forEach((o) => o.classList.remove('ds-select__option--active'));
      active.classList.add('ds-select__option--active');
      setActiveDescendant(root, active);
      if (focusOption) active.scrollIntoView({ block: 'nearest' });
    }
  }

  function closePanel(root, { returnFocus } = {}) {
    const { trigger, panel } = getEls(root);
    root.classList.remove('ds-select--open');
    trigger.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    setActiveDescendant(root, null);
    if (returnFocus) trigger.focus();
  }

  function selectOption(root, option, { silent } = {}) {
    const { label, meta, options } = getEls(root);
    if (!option) return;

    options.forEach((o) => o.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');

    const optionLabel = option.querySelector('.ds-select__option-label');
    const optionMeta = option.querySelector('.ds-select__option-meta');
    const triggerLabelText =
      option.getAttribute('data-trigger-label') || (optionLabel ? optionLabel.textContent : '');
    if (label) label.textContent = triggerLabelText;
    if (meta) {
      const metaText = optionMeta ? optionMeta.textContent : '';
      meta.textContent = metaText;
      meta.hidden = !metaText;
    }

    if (!silent) {
      root.dispatchEvent(
        new CustomEvent('ds-select:change', {
          bubbles: true,
          detail: { id: root.getAttribute('data-ds-select-id'), value: option.getAttribute('data-value') },
        })
      );
    }
  }

  function moveActive(root, delta) {
    const { options } = getEls(root);
    if (!options.length) return;
    const currentIndex = options.findIndex((o) =>
      o.classList.contains('ds-select__option--active')
    );
    let nextIndex = currentIndex + delta;
    if (nextIndex < 0) nextIndex = options.length - 1;
    if (nextIndex >= options.length) nextIndex = 0;

    options.forEach((o) => o.classList.remove('ds-select__option--active'));
    options[nextIndex].classList.add('ds-select__option--active');
    setActiveDescendant(root, options[nextIndex]);
    options[nextIndex].scrollIntoView({ block: 'nearest' });
  }

  function activeOption(root) {
    return root.querySelector('.ds-select__option--active');
  }

  function bindOptions(root) {
    const { options } = getEls(root);
    options.forEach((option) => {
      option.addEventListener('click', () => {
        selectOption(root, option);
        closePanel(root, { returnFocus: true });
      });
      option.addEventListener('mouseenter', () => {
        options.forEach((o) => o.classList.remove('ds-select__option--active'));
        option.classList.add('ds-select__option--active');
        setActiveDescendant(root, option);
      });
    });
  }

  function buildOptionMarkup(id, value, label, meta, selected, triggerLabel) {
    const li = document.createElement('li');
    li.className = 'ds-select__option';
    li.id = `ds-select-${id}-option-${value}`;
    li.setAttribute('role', 'option');
    li.setAttribute('data-value', value);
    li.setAttribute('data-ds-select-option', '');
    li.setAttribute('aria-selected', selected ? 'true' : 'false');
    li.setAttribute('tabindex', '-1');
    if (triggerLabel && triggerLabel !== label) {
      li.setAttribute('data-trigger-label', triggerLabel);
    }

    const labelSpan = document.createElement('span');
    labelSpan.className = 'ds-select__option-label';
    labelSpan.textContent = label;
    li.append(labelSpan);

    if (meta) {
      const metaSpan = document.createElement('span');
      metaSpan.className = 'ds-select__option-meta';
      metaSpan.textContent = meta;
      li.append(metaSpan);
    }

    return li;
  }

  /**
   * Rebuild this select's options from a plain array, e.g. when a consumer
   * (like the floating ATC bar) derives option data from another component's
   * rendered DOM rather than from this snippet's own Liquid params.
   * items: [{ value, label, meta, selected, triggerLabel }] — triggerLabel is
   * optional; when present it's shown in the trigger instead of `label` once
   * this option is selected (e.g. floating ATC bar appends "/mo").
   */
  function setOptions(root, items) {
    const { panel, label, meta } = getEls(root);
    const id = root.getAttribute('data-ds-select-id');
    panel.textContent = '';

    let selectedItem = null;
    items.forEach((item) => {
      panel.append(
        buildOptionMarkup(id, item.value, item.label, item.meta, item.selected, item.triggerLabel)
      );
      if (item.selected) selectedItem = item;
    });

    if (!selectedItem && items.length) selectedItem = items[0];
    if (selectedItem) {
      if (label) label.textContent = selectedItem.triggerLabel || selectedItem.label;
      if (meta) {
        meta.textContent = selectedItem.meta || '';
        meta.hidden = !selectedItem.meta;
      }
    }

    bindOptions(root);
  }

  function initRoot(root) {
    if (root.dataset.dsSelectInit) return;
    root.dataset.dsSelectInit = '1';

    const { trigger } = getEls(root);

    root.addEventListener('ds-select:options', (event) => {
      const items = (event.detail && event.detail.items) || [];
      setOptions(root, items);
    });

    trigger.addEventListener('click', () => {
      if (isOpen(root)) {
        closePanel(root, { returnFocus: false });
      } else {
        openPanel(root);
      }
    });

    trigger.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (isOpen(root)) {
            const active = activeOption(root);
            if (active) selectOption(root, active);
            closePanel(root, { returnFocus: true });
          } else {
            openPanel(root, { focusOption: true });
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen(root)) openPanel(root, { focusOption: true });
          else moveActive(root, 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen(root)) openPanel(root, { focusOption: true });
          else moveActive(root, -1);
          break;
        case 'Home':
          if (isOpen(root)) {
            event.preventDefault();
            const { options: opts } = getEls(root);
            opts.forEach((o) => o.classList.remove('ds-select__option--active'));
            if (opts[0]) {
              opts[0].classList.add('ds-select__option--active');
              setActiveDescendant(root, opts[0]);
              opts[0].scrollIntoView({ block: 'nearest' });
            }
          }
          break;
        case 'End':
          if (isOpen(root)) {
            event.preventDefault();
            const { options: opts } = getEls(root);
            opts.forEach((o) => o.classList.remove('ds-select__option--active'));
            const last = opts[opts.length - 1];
            if (last) {
              last.classList.add('ds-select__option--active');
              setActiveDescendant(root, last);
              last.scrollIntoView({ block: 'nearest' });
            }
          }
          break;
        case 'Escape':
          if (isOpen(root)) {
            event.preventDefault();
            closePanel(root, { returnFocus: true });
          }
          break;
        case 'Tab':
          if (isOpen(root)) closePanel(root, { returnFocus: false });
          break;
        default:
          break;
      }
    });

    bindOptions(root);

    document.addEventListener('click', (event) => {
      if (isOpen(root) && !root.contains(event.target)) {
        closePanel(root, { returnFocus: false });
      }
    });

    root.addEventListener('ds-select:set', (event) => {
      const { value } = event.detail || {};
      if (value == null) return;
      const { options: opts } = getEls(root);
      const match = opts.find((o) => o.getAttribute('data-value') === String(value));
      if (match) selectOption(root, match, { silent: true });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-ds-select]').forEach(initRoot);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  document.addEventListener('shopify:section:load', initAll);
})();
